import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const MgCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation worth it for MG Hector or Gloster?",
      answer:
        "Yes, especially during the initial years to reduce repair deductions.",
    },
    {
      question: "Do MG ZS EV premiums differ from petrol models?",
      answer:
        "Yes, EV insurance may vary due to battery costs and coverage structure.",
    },
    {
      question: "Which MG car has the lowest premium?",
      answer:
        "MG Astor usually has the lowest premium due to its compact size and lower IDV.",
    },
    {
      question: "Can I get insurance for a 5-year-old MG car?",
      answer:
        "Yes, both third-party and comprehensive plans are available for older vehicles.",
    },
    {
      question: "Does MG insurance cover ADAS components?",
      answer:
        "Coverage depends on the insurer. These may be included under comprehensive policies or specific add-ons.",
    },
    {
      question: "Is roadside assistance standard in MG plans?",
      answer: "Not always. It is usually offered as an optional add-on.",
    },
    {
      question: "Can I transfer my NCB to a new MG car?",
      answer:
        "Yes, No Claim Bonus is transferrable if you're the registered owner of the new car.",
    },
    {
      question: "Are MG car repairs costlier than others?",
      answer: "Slightly, due to imported parts and high-tech features.",
    },
    {
      question: "Do MG service centres offer cashless repairs?",
      answer:
        "Yes, most major insurers support cashless claims at authorized MG workshops.",
    },
    {
      question: "Can I buy MG insurance online?",
      answer:
        "Yes, you can compare and purchase policies through insurer websites or aggregators.",
    },
    {
      question: "What’s covered in a basic MG insurance plan?",
      answer:
        "A comprehensive policy covers accident damage, third-party liability, theft and natural disasters.",
    },
    {
      question: "Is insurance for MG ZS EV expensive?",
      answer:
        "It may be slightly higher than regular cars due to the battery and EV-specific components.",
    },
    {
      question: "Do I need separate EV insurance for ZS EV?",
      answer:
        "No, insurers offer EV-friendly comprehensive plans under the same structure.",
    },
    {
      question: "What happens if I cancel my MG policy early?",
      answer:
        "You may get a pro-rated refund depending on how many days the policy was active.",
    },
    {
      question: "Can accessories be covered in my MG policy?",
      answer:
        "Yes, declared accessories like infotainment units or alloy wheels can be included with extra premium.",
    },
    {
      question: "Are glass and windshield damages covered?",
      answer:
        "Yes, if the damage is accidental. Some insurers offer a windshield-specific cover too.",
    },
    {
      question: "Can I switch insurers for my MG car at renewal?",
      answer:
        "Yes, you are free to compare and change insurance providers when your policy ends.",
    },
    {
      question: "Is engine protection useful for MG owners?",
      answer:
        "Highly recommended, especially for Gloster or Hector Plus with diesel variants.",
    },
    {
      question: "Do MG cars qualify for NCB if not claimed?",
      answer:
        "Yes, all MG models qualify for No Claim Bonus if no claims are made in the previous policy year.",
    },
    {
      question: "What’s the penalty for uninsured MG cars?",
      answer:
        "Driving without valid insurance can result in fines from ₹2,000 to ₹4,000 and other legal consequences.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Recommended for high-value components like bumpers, lights and electronics.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Useful in case of total loss or theft of new vehicles.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Engine Protection",
      description:
        "Especially relevant for Gloster and Hector diesel variants.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Offers help during battery failure, tyre puncture or towing.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Covers fluids, filters, nuts and bolts used in repair jobs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
 const mginsurance = [
    {
      name: "Hector",
      path: "/mg-hector-car-insurance",
      image: "/assets/mghector.jpeg",
    },
       {
      name: "HectorPlus",
      path: "/mg-hector-plus-car-insurance",
      image: "/assets/mghectorplus.jpeg",
    },
     {
      name: "Astor",
      path: "/mg-astor-car-insurance",
      image: "/assets/mgastor.jpeg",
    },
    {
      name: "ZSEV",
      path: "/mg-zsev-car-insurance",
      image: "/assets/mgzsev.jpeg",
    },
    {
      name: "CometEV",
      path: "/mg-cometev-car-insurance",
      image: "/assets/mgcometev.jpeg",
    },
    {
      name: "Gloster",
      path: "/mg-gloster-car-insurance",
      image: "/assets/mggloster.jpeg",
    },
     {
      name: "RC6",
      path: "/mg-rc6-car-insurance",
      image: "/assets/mgrc6.jpeg",
    },
    {
      name: "MarvelX",
      path: "/mg-marvelx-car-insurance",
      image: "/assets/mgmarvelx.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>MG Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore MG car insurance options in India. Compare quotes, customize coverage, and buy online to protect your MG vehicle."
        />
        <meta
          name="keywords"
          content="mg car insurance, auto insurance india, mg hector insurance, mg zs ev insurance, car insurance quotes"
        />
            <link
          rel="canonical"
          href="https://thefinmaster.com/mg-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
   
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">MG Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">MG Car Insurance </span>{" "}
               
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                MG Motors has quickly gained popularity in India with its modern designs, advanced technology, and feature-rich models. Whether you drive a compact MG Astor or a spacious MG Gloster, securing your car with the right insurance is essential. This guide takes you through everything you need to know about insuring a MG Motors vehicle, from average premiums to model insights, useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/mginsurance.jpeg"
                alt="MG Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About MG in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Morris Garages, commonly known as MG, entered the Indian market with
            a fresh perspective on mobility. Known for its bold SUVs and EV
            innovation, MG has attracted buyers looking for value, features and
            futuristic technology. Its lineup includes models like the MG
            Hector, Astor, Gloster and ZS EV.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            With advanced tech and premium components, MG cars require insurance
            plans that offer strong protection and easy claim processes.
          </p>
        </div>
      </div>
        <div className="p-4 sm:p-8 bg-white">
              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                Choose Your Car Brand
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {mginsurance.map((calc, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.04 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link
                      to={calc.path}
                      className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                    >
                      <img
                        src={calc.image}
                        alt={calc.name}
                        className="w-16 h-16 object-contain mb-2"
                      />
                      <span className="text-md font-medium text-gray-800 break-words leading-snug">
                        {calc.name}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why MG Owners Need the Right Insurance Coverage
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            MG vehicles are equipped with intelligent features like ADAS
            systems, large infotainment screens and connected car technology.
            These enhance the experience but also increase the cost of repairs
            or replacements. A comprehensive insurance plan protects against:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Damage from road accidents or collisions</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Losses due to theft, riots or fire</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Flooding, hailstorms and other natural events</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>
                Legal or financial liability from third-party injury or damage
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>High-tech component replacements and labor costs</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Choosing a strong insurance partner also gives you access to
            cashless repairs at MG-authorized garages and relevant add-ons.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular MG Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here is an overview of MG’s top-selling models in India along with
          their approximate Insured Declared Value (IDV) and starting premium
          range for comprehensive coverage:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "MG Hector",
                category: "Mid-size SUV",
                idv: "15–21 Lakh",
                premium: "₹5,000 to ₹8,200",
              },
              {
                model: "MG Astor",
                category: "Compact SUV",
                idv: "10–16 Lakh",
                premium: "₹4,500 to ₹7,000",
              },
              {
                model: "MG ZS EV",
                category: "Electric SUV",
                idv: "20–24 Lakh",
                premium: "₹5,200 to ₹9,000",
              },
              {
                model: "MG Gloster",
                category: "Full-size SUV",
                idv: "30–36 Lakh",
                premium: "₹7,500 to ₹11,500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "MG Hector",
                    "Mid-size SUV",
                    "15–21 Lakh",
                    "₹5,000 to ₹8,200",
                  ],
                  ["MG Astor", "Compact SUV", "10–16 Lakh", "₹4,500 to ₹7,000"],
                  [
                    "MG ZS EV",
                    "Electric SUV",
                    "20–24 Lakh",
                    "₹5,200 to ₹9,000",
                  ],
                  [
                    "MG Gloster",
                    "Full-size SUV",
                    "30–36 Lakh",
                    "₹7,500 to ₹11,500",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons Worth Considering for MG Vehicles
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          MG owners can enhance protection and reduce out-of-pocket expenses
          with these common add-ons:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for MG Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          When buying insurance for MG vehicles, look for insurers with good
          customer service, a digital-first claim process and tie-ups with MG
          service centres:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers wide add-on coverage and network garages.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Great for premium SUVs like Gloster.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for strong electric car coverage like for ZS EV.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Competitive pricing and simple claim process.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Trusted brand with a wide garage network.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on MG Car Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              You can reduce your annual premium without compromising coverage
              by following these steps:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain a claim-free record to enjoy No Claim Bonus (NCB).
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install ARAI-approved anti-theft devices.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for voluntary deductibles if you're a confident driver.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Renew on time to avoid late fees or policy lapse.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare plans online to get the best rates.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for MG Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew MG car insurance, keep the following documents
              ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing policy document (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof like Aadhaar or PAN</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Passport-size photo (for offline or in-person applications)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          MG Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                MG cars are smart, stylish and feature-rich. Whether you own a
                compact Astor or a powerful Gloster, a reliable insurance policy
                gives you peace of mind against uncertainties on Indian roads.
                Look for comprehensive plans with essential add-ons and
                favorable claim service to keep your MG experience smooth and
                secure.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare, customise and choose the
                best MG insurance plans at competitive prices.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default MgCarInsurance;
